import { defineStore } from 'pinia';

export const useTableRowSortStore = defineStore('tableRowSort', {
  state: (): { rowOrders: Record<string, string[]> } => ({
    rowOrders: {}, // dataKey -> Array of IDs
  }),
  actions: {
    setRowOrder(dataKey: string, orderedIds: string[]) {
      this.rowOrders[dataKey] = orderedIds;
    },

    getRowOrder(dataKey: string): string[] {
      return this.rowOrders[dataKey] || [];
    },

    applyStoredOrder<T extends Record<string, any>>(dataKey: string, data: T[]): T[] {
      const storedOrder = this.getRowOrder(dataKey);

      if (storedOrder.length === 0 || data.length === 0) {
        return data;
      }

      for (let i = 0; i < storedOrder.length; i++) {
        const orderID = storedOrder[i];

        const itemIndex = data.findIndex(
          (row) => (row.id?.meta?.('rawvalue') ?? row.id) === orderID,
        );

        if (itemIndex !== -1 && itemIndex !== i) {
          const [movedItem] = data.splice(itemIndex, 1);

          if (movedItem !== undefined) {
            data.splice(i, 0, movedItem);
          }
        }
      }

      return data;
    },

    getSortFunction<T extends Partial<{ id: any }>>(dataKey?: string): (a: T, b: T) => number {
      if (!dataKey) return () => 0; // If no `dataKey`, default sorting (no change)

      const storedOrder = this.getRowOrder(dataKey);
      const orderMap = new Map(storedOrder.map((id, index) => [id, index]));

      return (a, b) => {
        const idA = a.id?.meta?.('rawvalue') ?? a.id;
        const idB = b.id?.meta?.('rawvalue') ?? b.id;

        if (idA === undefined || idB === undefined) return 0;

        const indexA = orderMap.get(idA) ?? storedOrder.length;
        const indexB = orderMap.get(idB) ?? storedOrder.length;

        return indexA - indexB;
      };
    },
  },
  persist: {
    storage: localStorage,
  },
});
